// src/utils/hockeyStatsParser.ts

import { Game, ScoredGame, Team } from '../types';
import { getBackendUrl } from '../config';
import { compareHeadToHead } from './hockeyUtils';

export const KNOWN_TEAM_LOGOS: Record<string, string> = {
    'Blues': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/2edf7418-4a3f-411f-6693-e17554b98400/256',
    'Cyclones': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/29b81ccc-941c-44a4-6114-e1494a4b7400/256',
    'Hawks': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/007ffe69-53cf-4ad0-1a35-6186e879f800/256',
    'Eagles': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/2d58f80e-3c30-43c9-e4e2-239c8c854900/256',
    'Express': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/403fd1e4-36e9-45ee-9e9f-99263c29f300/256',
    'Falcons': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/7f280c87-e16c-4dc5-c157-55bd11572a00/256',
    'Jets': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/2a059804-811f-4658-b065-669abc073800/256',
    'Knights': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/1f5cd72b-0e14-4e61-3da7-45a726cd1900/256',
    'Sabres': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/92f7d904-da9c-4b48-c068-4b34dbb92c00/256',
    'Sting': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/9fd4acf3-5ebc-4fea-be36-2644c71ef700/256',
    'Vipers': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/a0134e35-3f97-452d-6feb-c9352a079400/256',
    'Wilmette': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/a0358ea1-c027-4107-cddb-30903681a000/256',
    'Vikings': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/12e69ccc-29ce-434f-487e-9a8cdc5fe000/256',
    'Leafs': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/e383e18a-e04d-45db-53f3-a0867a80b100/256',
    'Winnetka': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/d6e2f0d9-db1b-4aac-9d06-ddb5d61f4700/256',
    'Chargers': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/56f69b4f-3c95-463c-2741-f4ef15d0ea00/256',
    'Flames': 'https://imagedelivery.net/ErrQpIaCOWR-Tz51PhN1zA/18d63c44-71a0-401c-cdc7-99bc18e50e00/256',
};

export function getTeamLogo(teamName: string, directLogoUrl?: string): string {
    if (directLogoUrl && directLogoUrl.trim() !== '') {
        return directLogoUrl.includes('/256') ? directLogoUrl : `${directLogoUrl}/256`;
    }
    const cleanName = teamName.trim();
    if (KNOWN_TEAM_LOGOS[cleanName]) {
        return KNOWN_TEAM_LOGOS[cleanName];
    }
    for (const [name, logo] of Object.entries(KNOWN_TEAM_LOGOS)) {
        if (cleanName.toLowerCase().includes(name.toLowerCase())) {
            return logo;
        }
    }
    return '';
}

/**
 * Evaluates whether a GameSheet game should be considered completed (either official final or unofficial final).
 * A game is considered completed if:
 * 1. It is officially marked as 'final' (isPendingFinal: false).
 * 2. Its status explicitly contains 'unofficial' (e.g. 'unofficial_final', 'unofficial') (isPendingFinal: true).
 * 3. Its status is 'in_progress' and it has been >= 2 hours since its scheduled start time
 *    (youth hockey slots are 60-80 min, so >= 2 hours guarantees the game on ice has concluded
 *    and is awaiting the scorekeeper upload or referee sign-off) (isPendingFinal: true).
 */
export function isGameCompleted(g: any, nowMs: number = Date.now()): { isCompleted: boolean; isPendingFinal: boolean } {
    const rawStatus = (g?.status || '').toLowerCase().trim();
    if (rawStatus === 'final') {
        return { isCompleted: true, isPendingFinal: false };
    }

    if (rawStatus.includes('unofficial')) {
        return { isCompleted: true, isPendingFinal: true };
    }

    if (rawStatus === 'in_progress') {
        let startTimeMs: number | null = null;
        if (g?.timeStampZulu) {
            const parsed = new Date(g.timeStampZulu).getTime();
            if (!isNaN(parsed)) startTimeMs = parsed;
        }
        if (!startTimeMs && g?.date && g?.time) {
            const parsed = new Date(`${g.date} ${g.time}`).getTime();
            if (!isNaN(parsed)) startTimeMs = parsed;
        }

        if (startTimeMs) {
            const elapsedHours = (nowMs - startTimeMs) / (1000 * 60 * 60);
            if (elapsedHours >= 2) {
                return { isCompleted: true, isPendingFinal: true };
            }
        }
    }

    return { isCompleted: false, isPendingFinal: false };
}

export interface LiveGameDetails {
    status: string;
    isUnofficial: boolean;
    homeGoals: number;
    visitorGoals: number;
    isOvertime: boolean;
    isShootout: boolean;
    scoresByPeriod: Array<{ title: string; home?: number; visitor?: number }>;
}

/**
 * Fetches real-time live game status and score from GameSheet's public Firestore document.
 * While games are in progress or in unofficial final status awaiting official referee/scorekeeper
 * sign-off, the REST API (unified-games) only has default 0-0 goals. The actual live score and period
 * breakdown are tracked in real-time in GameSheet's Firestore.
 */
export async function fetchLiveGameDetails(seasonId: string, gameId: string): Promise<LiveGameDetails | null> {
    try {
        const url = `https://firestore.googleapis.com/v1/projects/gamesheet-production/databases/(default)/documents/seasons/${seasonId}/games/${gameId}`;
        const res = await fetch(url);
        if (!res.ok) return null;
        const data = await res.json();
        const computed = data?.fields?.computed?.mapValue?.fields;
        if (!computed) return null;

        const status = (computed.status?.stringValue || '').toLowerCase().trim();
        const sb = computed.scoreboard?.mapValue?.fields;
        const tot = sb?.total?.mapValue?.fields;
        if (!tot) return null;

        const homeGoals = Number(tot.home?.integerValue ?? 0);
        const visitorGoals = Number(tot.visitor?.integerValue ?? 0);

        let isOvertime = Boolean(computed.hasOvertime?.booleanValue);
        const isShootout = Boolean(computed.hasShootout?.booleanValue);

        const scoresByPeriod: Array<{ title: string; home?: number; visitor?: number }> = [];
        const periodKeys = Object.keys(sb).filter(k => k !== 'total' && k !== 'so').sort();
        for (const k of periodKeys) {
            const periodField = sb[k]?.mapValue?.fields;
            const h = Number(periodField?.home?.integerValue ?? 0);
            const v = Number(periodField?.visitor?.integerValue ?? 0);
            const title = k === '04' ? 'OT' : (k === '05' ? '2OT' : String(parseInt(k, 10)));
            if ((k === '04' || k === '05') && (h > 0 || v > 0)) {
                isOvertime = true;
            }
            scoresByPeriod.push({ title, home: h, visitor: v });
        }

        return {
            status,
            isUnofficial: status.includes('unofficial'),
            homeGoals,
            visitorGoals,
            isOvertime,
            isShootout,
            scoresByPeriod
        };
    } catch {
        return null;
    }
}

/**
 * Fetches and processes all data for a given season and division.
 * @param seasonId The ID of the season.
 * @param divisionId The ID of the division.
 * @returns An object containing the processed standings, all games with scores, and the full schedule.
 */
export async function fetchAndProcessData(seasonId: string, divisionId: string) {
    const isUnmockedTestEnv =
        typeof process !== 'undefined' &&
        process.env?.NODE_ENV === 'test' &&
        !(global.fetch as any)?._isMockFunction;

    if (!isUnmockedTestEnv) {
        try {
            const unifiedRes = await fetch(
                `${getBackendUrl()}/api/standings?seasonId=${encodeURIComponent(seasonId)}&divisionId=${encodeURIComponent(divisionId)}`,
                { headers: { 'Accept': 'application/json' } }
            );
            if (unifiedRes && unifiedRes.ok) {
                const unifiedData = await unifiedRes.json();
                if (
                    unifiedData &&
                    Array.isArray(unifiedData.standings) &&
                    Array.isArray(unifiedData.allGames) &&
                    Array.isArray(unifiedData.fullSchedule)
                ) {
                    return {
                        standings: unifiedData.standings as Team[],
                        allGames: unifiedData.allGames as ScoredGame[],
                        fullSchedule: unifiedData.fullSchedule as Game[],
                    };
                }
            }
        } catch {
            // Transparently fall back to direct GameSheet + Firestore fetching
        }
    }

    const headers = {
        'Accept': 'application/json, text/plain, */*'
    };

    // Fetch standings and unified games in parallel
    const [standingsResponse, gamesResponse] = await Promise.all([
        fetch(`https://gamesheetstats.com/api/standings/${seasonId}?division=${divisionId}`, { headers }),
        fetch(`https://gamesheetstats.com/api/unified-games/${seasonId}?division=${divisionId}`, { headers })
    ]);

    if (!standingsResponse.ok || !gamesResponse.ok) {
        throw new Error(`Failed to fetch hockey data (Standings: ${standingsResponse.status}, Games: ${gamesResponse.status})`);
    }

    const standingsJson = await standingsResponse.json();
    const gamesJson = await gamesResponse.json();

    const divisionStandings = standingsJson?.data?.[0]?.standings || [];
    const rawGames: any[] = gamesJson?.data || [];

    // Query live game details for any in-progress games from GameSheet's live Firestore
    const inProgressGames = rawGames.filter((g: any) => (g?.status || '').toLowerCase() === 'in_progress');
    const liveDetailsMap = new Map<string, LiveGameDetails>();
    if (inProgressGames.length > 0) {
        await Promise.all(
            inProgressGames.map(async (g: any) => {
                const gameIdStr = String(g.gameId);
                const live = await fetchLiveGameDetails(seasonId, gameIdStr);
                if (live) {
                    liveDetailsMap.set(gameIdStr, live);
                }
            })
        );
    }

    // Normalize raw games into standard Game interface
    const fullSchedule: Game[] = rawGames.map((g: any) => {
        const home = g.home || {};
        const visitor = g.visitor || {};
        const gameIdStr = String(g.gameId);
        const live = liveDetailsMap.get(gameIdStr);

        const hPeriods = home.goalsByPeriod || {};
        const vPeriods = visitor.goalsByPeriod || {};
        const allPeriods = Array.from(new Set([...Object.keys(hPeriods), ...Object.keys(vPeriods)]));
        
        let scoresByPeriod = allPeriods
            .filter(p => p !== 'final')
            .map(p => ({
                title: p.toLowerCase().includes('so') ? 'SO' : (p.toLowerCase().includes('ot') ? 'OT' : p)
            }));

        const isOfficialFinal = g.status === 'final';
        const { isCompleted, isPendingFinal } = isGameCompleted(g);

        let finalScore: { homeGoals: number; visitorGoals: number } | undefined;
        let isTreatedAsFinal = false;
        let isUnofficialFinal = false;
        let isOvertime = false;
        let isShootout = false;

        if (isOfficialFinal && home.goals != null && visitor.goals != null) {
            finalScore = { homeGoals: Number(home.goals), visitorGoals: Number(visitor.goals) };
            isTreatedAsFinal = true;
            isOvertime = scoresByPeriod.some(p => p.title.toLowerCase().includes('ot')) || scoresByPeriod.length > 3;
            isShootout = scoresByPeriod.some(p => p.title.toLowerCase().includes('so'));
        } else if (live && (live.isUnofficial || isCompleted)) {
            finalScore = { homeGoals: live.homeGoals, visitorGoals: live.visitorGoals };
            isTreatedAsFinal = true;
            isUnofficialFinal = true;
            isOvertime = live.isOvertime;
            isShootout = live.isShootout;
            if (live.scoresByPeriod && live.scoresByPeriod.length > 0) {
                scoresByPeriod = live.scoresByPeriod.map(p => ({ title: p.title }));
            }
        } else if (isCompleted && home.goals != null && visitor.goals != null && (Number(home.goals) > 0 || Number(visitor.goals) > 0)) {
            // Only fall back to unified-games score if non-zero, to avoid treating placeholder 0-0 as a real tie
            finalScore = { homeGoals: Number(home.goals), visitorGoals: Number(visitor.goals) };
            isTreatedAsFinal = true;
            isUnofficialFinal = isPendingFinal;
            isOvertime = scoresByPeriod.some(p => p.title.toLowerCase().includes('ot')) || scoresByPeriod.length > 3;
            isShootout = scoresByPeriod.some(p => p.title.toLowerCase().includes('so'));
        }

        return {
            id: gameIdStr,
            gameId: gameIdStr,
            scheduleStartTime: g.timeStampZulu || '',
            status: isTreatedAsFinal ? 'final' : 'scheduled',
            isPendingFinal: isUnofficialFinal,
            isUnofficialFinal: isUnofficialFinal,
            isOvertime,
            isShootout,
            visitorTeam: {
                id: String(visitor.id || ''),
                name: visitor.title || ''
            },
            homeTeam: {
                id: String(home.id || ''),
                name: home.title || ''
            },
            time: g.time || '',
            date: g.date || '',
            location: g.location || '',
            finalScore,
            scoresByPeriod
        } as Game;
    });

    // Build allGames (ScoredGame format with game wrapper) for completed games
    const allGames: ScoredGame[] = fullSchedule
        .filter(g => g.status === 'final' && g.finalScore != null)
        .map(g => ({ game: g }));

    // Build a map of all team names to their IDs and logos
    const teamIdMap = new Map<string, string>();
    const teamLogoMap = new Map<string, string>();

    fullSchedule.forEach(game => {
        if (game.homeTeam?.id && game.homeTeam?.name) {
            teamIdMap.set(game.homeTeam.name, game.homeTeam.id);
        }
        if (game.visitorTeam?.id && game.visitorTeam?.name) {
            teamIdMap.set(game.visitorTeam.name, game.visitorTeam.id);
        }
    });

    const teamsMap = new Map<string, Team>();

    // Populate teams from standings data
    if (divisionStandings.length > 0) {
        divisionStandings.forEach((s: any) => {
            const teamTitle = s.team?.title || '';
            const rawLogo = s.team?.logoUrl || '';
            const logo = getTeamLogo(teamTitle, rawLogo);
            teamsMap.set(teamTitle, {
                id: String(s.team?.id || teamIdMap.get(teamTitle) || ''),
                name: teamTitle,
                logoUrl: logo,
                rank: 0,
                wins: 0,
                losses: 0,
                ties: 0,
                overtimeLosses: 0,
                points: 0,
                goalsScored: 0,
                totalGoalDifferential: 0
            });
            if (logo) {
                teamLogoMap.set(teamTitle, logo);
            }
        });
    }

    // Also ensure all teams appearing in schedule exist in teamsMap
    fullSchedule.forEach(game => {
        [game.homeTeam.name, game.visitorTeam.name].forEach(teamName => {
            if (teamName && !teamsMap.has(teamName)) {
                const logo = teamLogoMap.get(teamName) || getTeamLogo(teamName);
                teamsMap.set(teamName, {
                    id: teamIdMap.get(teamName) || '',
                    name: teamName,
                    logoUrl: logo,
                    rank: 0,
                    wins: 0,
                    losses: 0,
                    ties: 0,
                    overtimeLosses: 0,
                    points: 0,
                    goalsScored: 0,
                    totalGoalDifferential: 0
                });
            }
        });
    });

    // Calculate records and stats from scored games
    allGames.forEach(gameObj => {
        const game = gameObj.game;
        if (!game?.finalScore) return;

        const homeTeam = teamsMap.get(game.homeTeam.name);
        const visitorTeam = teamsMap.get(game.visitorTeam.name);

        if (homeTeam && visitorTeam) {
            const { homeGoals, visitorGoals } = game.finalScore;
            const periods = (game as any).scoresByPeriod?.length || 0;
            const isOtOrSo = Boolean(game.isOvertime || game.isShootout || periods > 3);

            if (homeGoals > visitorGoals) {
                homeTeam.wins++;
                visitorTeam.losses++;
                if (isOtOrSo) visitorTeam.overtimeLosses++;
            } else if (visitorGoals > homeGoals) {
                visitorTeam.wins++;
                homeTeam.losses++;
                if (isOtOrSo) homeTeam.overtimeLosses++;
            } else {
                homeTeam.ties++;
                visitorTeam.ties++;
            }
            homeTeam.goalsScored += homeGoals;
            visitorTeam.goalsScored += visitorGoals;
            homeTeam.totalGoalDifferential += (homeGoals - visitorGoals);
            visitorTeam.totalGoalDifferential += (visitorGoals - homeGoals);
        }
    });

    // Finalize points and sort the standings
    const teamsList = Array.from(teamsMap.values());
    teamsList.forEach(team => {
        team.points = (team.wins * 2) + team.ties + team.overtimeLosses;
    });

    teamsList.sort((a, b) => {
        if (a.points !== b.points) return b.points - a.points;
        const h2h = compareHeadToHead(a, b, allGames);
        if (h2h !== 0) return h2h;
        if (a.totalGoalDifferential !== b.totalGoalDifferential) return b.totalGoalDifferential - a.totalGoalDifferential;
        return b.goalsScored - a.goalsScored;
    });

    // Assign ranks, handling ties
    let rank = 1;
    for (let i = 0; i < teamsList.length; i++) {
        if (i > 0) {
            const current = teamsList[i];
            const previous = teamsList[i - 1];
            const areTied = current.points === previous.points &&
                            compareHeadToHead(current, previous, allGames) === 0 &&
                            current.totalGoalDifferential === previous.totalGoalDifferential &&
                            current.goalsScored === previous.goalsScored;
            if (!areTied) {
                rank = i + 1;
            }
        }
        teamsList[i].rank = rank;
    }

    return { standings: teamsList, allGames, fullSchedule };
}
